/**
 * Project UnderlineNav — GitHub-style primary chrome (2026-09-14 lock).
 *
 * Tabs under `/o/:org/p/:project`: Files · Issues · Changes · Agents · Settings.
 * Guide → Track remains the marketing / next-up pipeline (`workflow.ts`), not this rail.
 */

import {
  captureThinkPickerGesture,
  markThinkPickerPending,
  notifyThinkPickerPending,
} from "./think-picker-request.ts";
import { pathAfterProject } from "./workflow.ts";

export type ProjectTabId = "files" | "issues" | "changes" | "agents" | "settings";

export type ProjectTab = {
  id: ProjectTabId;
  label: string;
  /** Path under the project base. */
  to: string;
};

export const PROJECT_TABS: readonly ProjectTab[] = [
  { id: "files", label: "Files", to: "files" },
  { id: "issues", label: "Issues", to: "issues" },
  { id: "changes", label: "Changes", to: "changes" },
  { id: "agents", label: "Agents", to: "agents" },
  { id: "settings", label: "Settings", to: "settings" },
] as const;

export const PROJECT_TAB_IDS: readonly ProjectTabId[] = PROJECT_TABS.map((t) => t.id);

/** Mission beautify V1 tokens for project UnderlineNav (void canvas + copper active). */
export const UNDERLINE_NAV_CANVAS = "#050505";
export const UNDERLINE_NAV_HAIRLINE = "rgba(255, 255, 255, 0.10)";
export const UNDERLINE_NAV_ACTIVE = "#C45C26";
export const UNDERLINE_NAV_MUTED = "rgba(236, 236, 241, 0.48)";
export const UNDERLINE_NAV_FG = "#ECECF1";

export function projectTabHref(projectBase: string, to: string): string {
  const root = projectBase.replace(/\/+$/, "");
  if (!to) return root || "/";
  return `${root}/${to}`;
}

/**
 * First path segment after `/p/:project` when it is a known tab.
 * Unknown / empty → `files` (project home).
 */
export function projectTabSegment(pathname: string): ProjectTabId {
  const rest = pathAfterProject(pathname);
  if (rest === null || rest === "") return "files";
  const first = rest.split("/")[0] ?? "";
  if ((PROJECT_TAB_IDS as readonly string[]).includes(first)) return first as ProjectTabId;
  const highlighted = projectTabHighlight(pathname);
  return highlighted ?? "files";
}

/** Preserve the current tab when switching projects in the crumb. */
export function projectSwitchHref(orgBase: string, projectSlug: string, pathname: string): string {
  const org = orgBase.replace(/\/+$/, "");
  return `${org}/p/${projectSlug}/${projectTabSegment(pathname)}`;
}

export function projectTabHighlight(pathname: string): ProjectTabId | null {
  const rest = pathAfterProject(pathname);
  if (rest === null) return null;
  if (
    rest === "" ||
    rest === "files" ||
    rest.startsWith("files/") ||
    rest === "hardware" ||
    rest.startsWith("hardware/")
  ) {
    return "files";
  }
  if (
    rest === "issues" ||
    rest.startsWith("issues/") ||
    rest === "milestones" ||
    rest.startsWith("milestones/") ||
    rest === "releases" ||
    rest.startsWith("releases/")
  ) {
    return "issues";
  }
  if (rest === "changes" || rest.startsWith("changes/") || rest === "reviews" || rest.startsWith("reviews/")) {
    return "changes";
  }
  if (rest === "agents" || rest.startsWith("agents/") || rest === "mind" || rest.startsWith("mind/")) {
    return "agents";
  }
  if (rest === "settings" || rest.startsWith("settings/") || rest === "help/mcp" || rest.startsWith("help/mcp")) {
    return "settings";
  }
  return null;
}

export function isProjectTabPath(pathname: string): boolean {
  return projectTabHighlight(pathname) !== null || pathAfterProject(pathname) !== null;
}

/** Think dock auto-opens on Changes (reviews) and Agents. */
export function thinkOpensByDefault(pathname: string): boolean {
  const rest = pathAfterProject(pathname);
  if (rest === null) return false;
  return (
    rest === "changes" ||
    rest.startsWith("changes/") ||
    rest === "reviews" ||
    rest.startsWith("reviews/") ||
    rest === "agents" ||
    rest.startsWith("agents/")
  );
}

export function filesHref(projectBase: string, hash = ""): string {
  return `${projectTabHref(projectBase, "files")}${hash}`;
}

export function changesHref(
  projectBase: string,
  review?: { id: string; from_ref: string; to_ref: string } | string,
  search = "",
): string {
  const root = projectTabHref(projectBase, "changes");
  if (!review) return `${root}${search}`;
  if (typeof review === "string") return `${root}/${review}${search}`;
  return `${root}/${review.id}?from_ref=${encodeURIComponent(review.from_ref)}&to_ref=${encodeURIComponent(review.to_ref)}`;
}

/** Issue detail URL always carries git_ref (tracker, not a Change). */
export function issuesHref(
  projectBase: string,
  issue?: { id: string; git_ref: string } | string,
  search = "",
): string {
  const root = projectTabHref(projectBase, "issues");
  if (!issue) return `${root}${search}`;
  if (typeof issue === "string") {
    const suffix = search.startsWith("?") || search === "" ? search : `?${search}`;
    return `${root}/${issue}${suffix}`;
  }
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  if (issue.git_ref) params.set("git_ref", issue.git_ref);
  const qs = params.toString();
  return `${root}/${issue.id}${qs ? `?${qs}` : ""}`;
}

export const OPEN_THINK_EVENT = "mission:open-think";
export const OPEN_THINK_PICKER_EVENT = "mission:open-think-picker";

export function requestOpenThink(): void {
  try {
    sessionStorage.setItem("mx.think", "1");
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new Event(OPEN_THINK_EVENT));
}

/**
 * Single entry for Files upload / `#upload` / M6.
 * Persists a pending-picker flag so a Sheet that mounts on the next render
 * still sees the request. The dock consumes the flag; see think-picker-request.ts.
 */
export function requestOpenThinkPicker(): void {
  const gesture = captureThinkPickerGesture();
  markThinkPickerPending();
  requestOpenThink();
  window.dispatchEvent(new CustomEvent(OPEN_THINK_PICKER_EVENT, { detail: { gesture } }));
  notifyThinkPickerPending();
}
